import { logger } from './logger';
import { SubscriptionLimitError, SubscriptionAuthError } from './api-client';
import { ValidationError } from './validation';

export type ADHDErrorType = 
  | 'TASK_NOT_FOUND'
  | 'PROJECT_NOT_FOUND'
  | 'BODY_DOUBLING_FAILED'
  | 'ENERGY_CONTEXT_INVALID'
  | 'BREAK_REMINDER_FAILED'
  | 'PRIORITIZATION_FAILED'
  | 'STORAGE_ERROR'
  | 'API_ERROR'
  | 'VALIDATION_ERROR'
  | 'TIMEOUT_ERROR'
  | 'UNKNOWN_ERROR';

export interface ADHDError {
  type: ADHDErrorType;
  message: string;
  userMessage: string;
  suggestions?: string[];
  context?: Record<string, any>;
  timestamp: Date;
  isRecoverable: boolean;
}

export class ADHDErrorHandler {
  private static errorMessages: Record<ADHDErrorType, {
    message: string;
    userMessage: string;
    suggestions: string[];
    isRecoverable: boolean;
  }> = {
    TASK_NOT_FOUND: {
      message: 'Task with specified ID not found',
      userMessage: 'I couldn\'t find that task. It might have been deleted or the ID changed.',
      suggestions: [
        'Try asking me to list your current tasks',
        'Double-check the task name or description',
        'The task might have been completed and archived'
      ],
      isRecoverable: true
    },
    PROJECT_NOT_FOUND: {
      message: 'Project with specified ID not found',
      userMessage: 'I couldn\'t find that project. It might have been deleted or renamed.',
      suggestions: [
        'Ask me to show your current projects',
        'Try creating a new project with that name',
        'Check if the project was recently deleted'
      ],
      isRecoverable: true
    },
    BODY_DOUBLING_FAILED: {
      message: 'Body doubling session could not be started',
      userMessage: 'I had trouble starting your focus session. This might be due to conflicting sessions or task issues.',
      suggestions: [
        'Make sure no other focus session is active',
        'Try selecting a different task',
        'Check that the task exists and is accessible'
      ],
      isRecoverable: true
    },
    ENERGY_CONTEXT_INVALID: {
      message: 'Invalid energy context parameters provided',
      userMessage: 'I couldn\'t set your energy context with those details. Please provide a valid energy level.',
      suggestions: [
        'Use "low", "medium", or "high" for energy level',
        'Try: "Set my energy to medium"',
        'Make sure all information is clear and specific'
      ],
      isRecoverable: true
    },
    BREAK_REMINDER_FAILED: {
      message: 'Failed to configure hyperfocus break reminders',
      userMessage: 'I couldn\'t set up your break reminders. There might be an issue with the timing or settings.',
      suggestions: [
        'Try setting a different break interval (15-60 minutes)',
        'Choose a different break type: water, movement, eyes, or full',
        'Make sure your browser allows notifications'
      ],
      isRecoverable: true
    },
    PRIORITIZATION_FAILED: {
      message: 'Task prioritization analysis failed',
      userMessage: 'I had trouble analyzing task priorities. This might be due to missing task information.',
      suggestions: [
        'Try adding more details to your tasks',
        'Set due dates where applicable',
        'Provide task descriptions to improve analysis'
      ],
      isRecoverable: true
    },
    STORAGE_ERROR: {
      message: 'Failed to save or retrieve data from local storage',
      userMessage: 'I couldn\'t save your changes. This might be due to storage space or browser restrictions.',
      suggestions: [
        'Try clearing some browser storage space',
        'Refresh the page and try again',
        'Check if your browser allows local storage'
      ],
      isRecoverable: true
    },
    API_ERROR: {
      message: 'API request failed',
      userMessage: 'I couldn\'t complete that request due to a connection or server issue.',
      suggestions: [
        'Check your internet connection',
        'Try again in a moment',
        'Some features might be temporarily unavailable'
      ],
      isRecoverable: true
    },
    VALIDATION_ERROR: {
      message: 'Input validation failed',
      userMessage: 'Some of the information you provided doesn\'t look quite right.',
      suggestions: [
        'Double-check dates are in a valid format',
        'Make sure required fields aren\'t empty',
        'Try rephrasing your request'
      ],
      isRecoverable: true
    },
    TIMEOUT_ERROR: {
      message: 'Operation timed out',
      userMessage: 'That took longer than expected and timed out. The system might be busy.',
      suggestions: [
        'Try the same action again',
        'Break complex requests into smaller parts',
        'Wait a moment and retry'
      ],
      isRecoverable: true
    },
    UNKNOWN_ERROR: {
      message: 'An unexpected error occurred',
      userMessage: 'Something unexpected happened. Don\'t worry, your data should be safe.',
      suggestions: [
        'Try refreshing the page',
        'Restart the action you were performing',
        'If this keeps happening, try restarting your browser'
      ],
      isRecoverable: true
    }
  };

  /**
   * Handle and format an error for ADHD users with helpful context
   */
  static handle(error: Error | ADHDError | any, context?: Record<string, any>): ADHDError {
    let adhdError: ADHDError;

    if (this.isADHDError(error)) {
      adhdError = error;
    } else {
      // Convert generic error to ADHD error
      const errorType = this.classifyError(error);
      const errorInfo = this.errorMessages[errorType];
      
      adhdError = {
        type: errorType,
        message: error.message || error.toString(),
        userMessage: errorInfo.userMessage,
        suggestions: errorInfo.suggestions,
        context: { ...context, originalError: error.toString() },
        timestamp: new Date(),
        isRecoverable: errorInfo.isRecoverable
      };
    }

    // Log for debugging
    logger.error('ADHD Error occurred', {
      type: adhdError.type,
      message: adhdError.message,
      context: adhdError.context,
      isRecoverable: adhdError.isRecoverable
    });

    return adhdError;
  }

  /**
   * Create a specific ADHD error
   */
  static create(
    type: ADHDErrorType, 
    customMessage?: string, 
    context?: Record<string, any>,
    additionalSuggestions?: string[]
  ): ADHDError {
    const errorInfo = this.errorMessages[type];
    
    return {
      type,
      message: customMessage || errorInfo.message,
      userMessage: errorInfo.userMessage,
      suggestions: additionalSuggestions ? 
        [...errorInfo.suggestions, ...additionalSuggestions] : 
        errorInfo.suggestions,
      context,
      timestamp: new Date(),
      isRecoverable: errorInfo.isRecoverable
    };
  }

  /**
   * Get user-friendly error message with suggestions
   */
  static getFormattedMessage(error: ADHDError): {
    title: string;
    message: string;
    suggestions: string[];
    canRetry: boolean;
  } {
    return {
      title: `${error.type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())} Issue`,
      message: error.userMessage,
      suggestions: error.suggestions || [],
      canRetry: error.isRecoverable
    };
  }

  private static isADHDError(error: any): error is ADHDError {
    return error && typeof error === 'object' && 'type' in error && 'userMessage' in error;
  }

  private static classifyError(error: any): ADHDErrorType {
    const message = error.message?.toLowerCase() || error.toString().toLowerCase();
    
    if (message.includes('not found') && message.includes('task')) {
      return 'TASK_NOT_FOUND';
    }
    
    if (message.includes('not found') && message.includes('project')) {
      return 'PROJECT_NOT_FOUND';
    }
    
    if (message.includes('body doubling') || message.includes('session')) {
      return 'BODY_DOUBLING_FAILED';
    }
    
    if (message.includes('energy') || message.includes('context')) {
      return 'ENERGY_CONTEXT_INVALID';
    }
    
    if (message.includes('break') || message.includes('reminder')) {
      return 'BREAK_REMINDER_FAILED';
    }
    
    if (message.includes('priority') || message.includes('eisenhower')) {
      return 'PRIORITIZATION_FAILED';
    }
    
    if (message.includes('storage') || message.includes('localstorage')) {
      return 'STORAGE_ERROR';
    }
    
    if (message.includes('network') || message.includes('fetch') || message.includes('api')) {
      return 'API_ERROR';
    }
    
    if (message.includes('validation') || message.includes('invalid')) {
      return 'VALIDATION_ERROR';
    }
    
    if (message.includes('timeout') || message.includes('timed out')) {
      return 'TIMEOUT_ERROR';
    }
    
    return 'UNKNOWN_ERROR';
  }
}

export default ADHDErrorHandler;

/**
 * Centralized error handling for creation operations (tasks and projects)
 * Provides consistent user-friendly error messages and categorizes errors by type
 */

export interface ErrorHandlerOptions {
  /** Show success toast on success */
  showSuccess?: (title: string, message: string) => void;
  /** Show error toast on failure */
  showError: (title: string, message: string) => void;
  /** Show warning toast for partial failures */
  showWarning?: (title: string, message: string) => void;
  /** Custom success message for this operation */
  successMessage?: string;
  /** Entity type being created ('task' | 'project') */
  entityType: 'task' | 'project';
}

/**
 * Handles creation errors with user-friendly messages and appropriate toasts
 */
export const handleCreationError = (
  error: unknown,
  options: ErrorHandlerOptions
): void => {
  const { showError, entityType } = options;

  // Subscription limit errors
  if (error instanceof SubscriptionLimitError) {
    showError(
      `${capitalizeFirst(entityType)} Limit Reached`,
      `You've reached your ${entityType} limit. Upgrade for unlimited access!`
    );
    return;
  }

  // Authentication errors
  if (error instanceof SubscriptionAuthError) {
    showError(
      'Authentication Required',
      `Please sign in to create ${entityType}s`
    );
    return;
  }

  // Validation errors
  if (error instanceof ValidationError) {
    showError(
      'Invalid Input',
      error.userMessage || `Please check your ${entityType} details`
    );
    return;
  }

  // Network/API errors
  if (error instanceof Error) {
    if (error.message.toLowerCase().includes('network') || 
        error.message.toLowerCase().includes('fetch')) {
      showError(
        'Connection Issue',
        'Please check your internet connection and try again'
      );
      return;
    }

    if (error.message.toLowerCase().includes('timeout')) {
      showError(
        'Request Timeout',
        'The request took too long. Please try again'
      );
      return;
    }

    // Generic error with error message
    showError(
      `Failed to Create ${capitalizeFirst(entityType)}`,
      error.message || 'Something went wrong. Please try again.'
    );
    return;
  }

  // Unknown/Generic errors
  showError(
    `Failed to Create ${capitalizeFirst(entityType)}`,
    'Something went wrong. Please try again.'
  );
};

/**
 * Handles success scenarios with appropriate messaging
 */
export const handleCreationSuccess = (
  result: { success: boolean; [key: string]: any },
  options: ErrorHandlerOptions
): void => {
  const { showSuccess, entityType, successMessage } = options;

  if (showSuccess && result.success) {
    const entityName = result[entityType]?.title || result[entityType]?.name || '';
    const defaultMessage = entityName 
      ? `"${entityName}" ${entityType} created successfully`
      : `${capitalizeFirst(entityType)} created successfully`;
    
    showSuccess(
      `${capitalizeFirst(entityType)} Created`,
      successMessage || defaultMessage
    );
  }
};

/**
 * Comprehensive error handler for async creation operations
 * Combines error handling and success handling in one function
 */
export const handleAsyncCreation = async <T>(
  operation: () => Promise<T>,
  options: ErrorHandlerOptions
): Promise<T | null> => {
  try {
    const result = await operation();
    
    // Handle success if result has success property
    if (result && typeof result === 'object' && 'success' in result) {
      if ((result as any).success) {
        handleCreationSuccess(result as any, options);
      } else {
        // Result indicates failure
        const errorMessage = (result as any).error || (result as any).message;
        handleCreationError(new Error(errorMessage), options);
        return null;
      }
    }
    
    return result;
  } catch (error) {
    handleCreationError(error, options);
    return null;
  }
};

/**
 * Retry utility with exponential backoff for transient failures
 */
export const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      if (i === maxRetries - 1) throw error;
      
      // Don't retry certain error types
      if (error instanceof SubscriptionLimitError || 
          error instanceof SubscriptionAuthError ||
          error instanceof ValidationError) {
        throw error;
      }
      
      const delay = baseDelay * Math.pow(2, i);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  throw new Error('Max retries exceeded');
};

/**
 * Development error simulation for testing
 */
export const simulateError = (errorType: 'network' | 'subscription' | 'validation' | 'timeout'): never => {
  switch (errorType) {
    case 'network':
      throw new Error('Network error: Failed to fetch');
    case 'subscription':
      throw new SubscriptionLimitError('todos', 5, 5);
    case 'validation':
      throw new ValidationError('Title is required', 'title', 'Please enter a title');
    case 'timeout':
      throw new Error('Request timeout: The request took too long');
    default:
      throw new Error('Unknown error type');
  }
};

// Utility functions
const capitalizeFirst = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Development-only error triggers
if (process.env.NODE_ENV === 'development') {
  (window as any).__simulateError = {
    network: () => simulateError('network'),
    subscription: () => simulateError('subscription'),
    validation: () => simulateError('validation'),
    timeout: () => simulateError('timeout')
  };
}